import type { IHasId } from 'tm3-core/api.dtos';

export {};

declare global {
  interface Array<T> {
    addItems(this: T[], ...items: T[]): T[];
    addIf(this: T[], add?: boolean, target?: T | null, fallback?: T | null): T[];
    addNotEmpty(this: T[], target?: T | null): T[];
    average(this: T[], callback: (value: T, index: number, array: T[]) => number): number;
    intersect(this: T[], target: Iterable<T>): T[];
    max(this: T[], callback: (value: T, index: number, array: T[]) => number): number;
    min(this: T[], callback: (value: T, index: number, array: T[]) => number): number;
    removeItem(this: T[], target: T): boolean;
    sum(this: T[], callback: (value: T, index: number, array: T[]) => number): number;
    toClassNames<T>(this: T[]): string;
    toMap<U>(this: T[], callback: (value: T, index: number, array: T[]) => U): Map<U, T>;
    toObject<U extends string | number | symbol>(
      this: T[],
      callback: (value: T, index: number, array: T[]) => U
    ): Record<U, T>;
    toSet(this: T[]): Set<T>;
    updateById(this: T[], value: T & IHasId): T[];
    updateItem(this: T[], value: T, pred: (value: T, index: number, array: T[]) => boolean): T[];
    unZip(this: T[], pred: (value: T, index: number, array: T[]) => boolean): [T[], T[]];
    /**
     * @deprecated Prefer Map.groupBy()
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map/groupBy
     */
    groupToMap<U>(this: T[], keyFunc: (value: T, index: number, array: T[]) => U): Map<U, T[]>;
  }
}

const { prototype } = Array;

if (!prototype.addItems) {
  Object.defineProperty(prototype, 'addItems', {
    writable: false,
    enumerable: false,
    value: function <T>(this: T[], ...items: T[]): T[] {
      this.push(...items);

      return this;
    }
  });
}

if (!prototype.addIf) {
  Object.defineProperty(prototype, 'addIf', {
    writable: false,
    enumerable: false,
    value: function <T>(
      this: T[],
      add?: boolean,
      target?: T | null,
      fallback?: T | null
    ): Array<T> {
      return this.addNotEmpty(add ? target : fallback);
    }
  });
}

if (!prototype.addNotEmpty) {
  Object.defineProperty(prototype, 'addNotEmpty', {
    writable: false,
    enumerable: false,
    value: function <T>(this: T[], target?: T | null): T[] {
      return !!target ? this.addItems(target) : this;
    }
  });
}

if (!prototype.average) {
  Object.defineProperty(prototype, 'average', {
    writable: false,
    enumerable: false,
    value: function <T>(
      this: T[],
      callback: (value: T, index: number, array: T[]) => number
    ): number {
      return this.sum(callback) / this.length;
    }
  });
}

if (!prototype.intersect) {
  Object.defineProperty(prototype, 'intersect', {
    writable: false,
    enumerable: false,
    value: function <T>(this: T[], target: Iterable<T>): T[] {
      return (this as T[]).toSet().intersect(target);
    }
  });
}

if (!prototype.max) {
  Object.defineProperty(prototype, 'max', {
    writable: false,
    enumerable: false,
    value: function <T>(
      this: T[],
      callback: (value: T, index: number, array: T[]) => number
    ): number {
      return this.reduce((acc, ...args) => {
        return Math.max(acc, callback(...args));
      }, Number.NEGATIVE_INFINITY);
    }
  });
}

if (!prototype.min) {
  Object.defineProperty(prototype, 'min', {
    writable: false,
    enumerable: false,
    value: function <T>(
      this: T[],
      callback: (value: T, index: number, array: T[]) => number
    ): number {
      return this.reduce((acc, ...args) => {
        return Math.min(acc, callback(...args));
      }, Number.POSITIVE_INFINITY);
    }
  });
}

if (!prototype.removeItem) {
  Object.defineProperty(prototype, 'removeItem', {
    writable: false,
    enumerable: false,
    value: function <T>(this: T[], target: Iterable<T>): boolean {
      const index = this.findIndex((x) => x === target);

      const canRemove = index > -1;

      if (canRemove) {
        this.splice(index, 1);
      }

      return canRemove;
    }
  });
}

if (!prototype.sum) {
  Object.defineProperty(prototype, 'sum', {
    writable: false,
    enumerable: false,
    value: function <T>(
      this: T[],
      callback: (value: T, index: number, array: T[]) => number
    ): number {
      return this.reduce((acc, ...args) => {
        return acc + callback(...args);
      }, 0);
    }
  });
}

if (!prototype.toClassNames) {
  Object.defineProperty(prototype, 'toClassNames', {
    writable: false,
    enumerable: false,
    value: function <T>(this: T[]): string {
      return this.join(' ');
    }
  });
}

if (!prototype.toMap) {
  Object.defineProperty(prototype, 'toMap', {
    writable: false,
    enumerable: false,
    value: function <T, U>(
      this: T[],
      callback: (value: T, index: number, array: T[]) => U
    ): Map<U, T> {
      return this.reduce((map: Map<U, T>, value, index, array) => {
        return map.set(callback(value, index, array), value);
      }, new Map<U, T>());
    }
  });
}

if (!prototype.toObject) {
  Object.defineProperty(prototype, 'toObject', {
    writable: false,
    enumerable: false,
    value: function <T, U extends string | number | symbol>(
      this: T[],
      callback: (value: T, index: number, array: T[]) => U
    ): Record<U, T> {
      return this.reduce<Record<U, T>>((acc, value, index, array) => {
        acc[callback(value, index, array)] = value;
        return acc;
      }, {} as Record<U, T>);
    }
  });
}

if (!prototype.toSet) {
  Object.defineProperty(prototype, 'toSet', {
    writable: false,
    enumerable: false,
    value: function <T>(this: T[]): Set<T> {
      return new Set(this);
    }
  });
}

if (!prototype.unZip) {
  Object.defineProperty(prototype, 'unZip', {
    writable: false,
    enumerable: false,
    value: function <T>(
      this: T[],
      pred: (value: T, index: number, array: T[]) => boolean
    ): [T[], T[]] {
      const left: T[] = [];
      const right: T[] = [];

      this.forEach((value, index, array) => {
        (pred(value, index, array) ? left : right).push(value);
      });

      return [left, right];
    }
  });
}

if (!prototype.updateItem) {
  Object.defineProperty(prototype, 'updateItem', {
    writable: false,
    enumerable: false,
    value: function <T>(
      this: T[],
      value: T,
      pred: (value: T, index: number, array: T[]) => boolean
    ): T[] {
      const index = this.findIndex(pred);

      if (index > -1) {
        this[index] = value;
      } else {
        this.push(value);
      }

      return this;
    }
  });
}

if (!prototype.updateById) {
  Object.defineProperty(prototype, 'updateById', {
    writable: false,
    enumerable: false,
    value: function <T extends IHasId>(this: T[], value: T): T[] {
      return this.updateItem(value, (x: T) => x.id === value.id);
    }
  });
}

if (!prototype.groupToMap) {
  Object.defineProperty(prototype, 'groupToMap', {
    writable: false,
    enumerable: false,
    value: function <T, U>(
      this: T[],
      callback: (value: T, index: number, array: T[]) => U
    ): Map<U, T[]> {
      return this.reduce((map: Map<U, T[]>, value, index, array) => {
        const key = callback(value, index, array);
        const values = (map.get(key) ?? []).addItems(value);
        return map.set(key, values);
      }, new Map<U, T[]>());
    }
  });
}
